import civilCompanyIcon from '@repo/visual-assets/illustrations/ill-sspot-f-24-pagopa-imu.svg';
import freelancerIcon from '@repo/visual-assets/illustrations/ill-sspot-account-freelancer.svg';
import microbusinessIcon from '@repo/visual-assets/illustrations/ill-sspot-account-microbusiness.svg';
import { PRODUCT_GROUP_CODES } from '@repo/pricing-kit/constants';

export const ASSOCIATIONS_CODES = [
  '7321',
  '7323',
  '9150',
  '9220',
  '9221',
  '9222',
  '9223', // Not association, but behaves the same - Groupement d'employeurs
  '9224',
  '9230',
  '9300', // Not association, but behaves the same - Fondation
  'AC',
  'DE602',
  'BE017',
] as const;

export const GERMAN_BEC_ELIGIBLE_COMPANY_TYPES = [
  'DE107', // GmbH & Co. KG
  'DE103', // Kommanditgesellschaft (KG)
  'DE201', // AG
  'DE206', // GmbH
  'DE202', // KGaA
  'DE112', // UG & Co. KG
  'DE108', // AG & Co. KG
] as const;

/**
 * Enum that defines the initial organization type
 * selected by the user at the beginning of the flow.
 */
export const ORGANIZATION_TYPES = {
  FREELANCER: 'freelancer',
  SMALL_BUSINESS: 'small_business',
  /** Currently used only for DE */
  COMPANY: 'company',
  BIG_COMPANY: 'big_company',
  ASSOCIATION: 'association',
  WORKS_COUNCIL: 'works_council',
} as const;

export type OrganizationType = (typeof ORGANIZATION_TYPES)[keyof typeof ORGANIZATION_TYPES];

export const EMPLOYEE_RANGES = {
  ZERO: '0',
  ONE_TO_FOUR: '1-4',
  FIVE_TO_NINE: '5-9',
  ONE_TO_NINE: '1-9',
  ONE_PLUS: '1+',
  TEN_PLUS: '10+',
  ZERO_PLUS: '0+',
  ELEVEN_TO_FORTY_NINE: '11-49',
} as const;

export type EmployeeRange = (typeof EMPLOYEE_RANGES)[keyof typeof EMPLOYEE_RANGES];

export const ORGANIZATION_EMPLOYEES_RANGE = {
  [ORGANIZATION_TYPES.FREELANCER]: EMPLOYEE_RANGES.ZERO,
  [ORGANIZATION_TYPES.SMALL_BUSINESS]: EMPLOYEE_RANGES.ONE_TO_NINE,
  [ORGANIZATION_TYPES.COMPANY]: EMPLOYEE_RANGES.ONE_PLUS,
  [ORGANIZATION_TYPES.BIG_COMPANY]: EMPLOYEE_RANGES.TEN_PLUS,
  [ORGANIZATION_TYPES.ASSOCIATION]: EMPLOYEE_RANGES.ZERO_PLUS,
  // NOTE: the range for works_council can be set by the user in organization/info form to '11-49' or '50+'
  [ORGANIZATION_TYPES.WORKS_COUNCIL]: EMPLOYEE_RANGES.ELEVEN_TO_FORTY_NINE,
} as const;

export const EMPLOYEE_RANGE_TO_ORG_TYPE: Record<string, OrganizationType> = {
  [EMPLOYEE_RANGES.ZERO]: ORGANIZATION_TYPES.FREELANCER,
  [EMPLOYEE_RANGES.ONE_TO_FOUR]: ORGANIZATION_TYPES.SMALL_BUSINESS,
  [EMPLOYEE_RANGES.FIVE_TO_NINE]: ORGANIZATION_TYPES.SMALL_BUSINESS,
  [EMPLOYEE_RANGES.ONE_TO_NINE]: ORGANIZATION_TYPES.SMALL_BUSINESS,
  [EMPLOYEE_RANGES.ONE_PLUS]: ORGANIZATION_TYPES.COMPANY,
  [EMPLOYEE_RANGES.TEN_PLUS]: ORGANIZATION_TYPES.BIG_COMPANY,
  [EMPLOYEE_RANGES.ZERO_PLUS]: ORGANIZATION_TYPES.ASSOCIATION,
  [EMPLOYEE_RANGES.ELEVEN_TO_FORTY_NINE]: ORGANIZATION_TYPES.WORKS_COUNCIL,
};

export const CONTRACT_SIGN_STATUS = {
  PENDING: 'pending',
  VALIDATION_ERROR: 'validation_error',
  COMPLETED: 'completed',
} as const;
export type ContractSignStatus = (typeof CONTRACT_SIGN_STATUS)[keyof typeof CONTRACT_SIGN_STATUS];

export const FRENCH_LEGAL_FORMS = [
  '5720',
  '5499',
  '5710',
  '1000',
  '5498',
  '1500',
  '1300',
  '6540',
  '6599',
  '5599',
  '5202',
  '5306',
  '3120',
  '6220',
  '9224',
  '9220',
  '9221',
  '9230',
  '9222',
  '7321',
  '5485',
] as const;

export const FRENCH_SOLO_LEGAL_FORMS = [
  '5720', // EURL
  '5498', // SASU
];

export const EUROPEAN_STANDARD_REGISTER_LEGAL_FORMS = {
  AT: ['AT202', 'AT203', 'ATCUST301', 'AT102', 'AT103'],
  NL: ['NL101', 'NLCUST208', 'NL206', 'NL304'],
  BE: ['BE610', 'BECUST201', 'BE011', 'BE012', 'BE014', 'BE017'],
  PT: ['PT121', 'PTCUST183', 'PTCUST184'],
} as const;

export const PHYSICAL_PERSON_COMPANY_TYPES = [
  '1000',
  '1100',
  '1200',
  '1300',
  '1500',
  '1600',
  '1700',
  '1900',
  'AC',
  'DI',
  'IF',
  'PF',
  'IT001',
  'IT002',
  'DE001',
  'DE002',
  'DE210',
  'ESC01',
  'ESC02',
] as const;

export const GBR_DE_MIN_PARTNERS_COUNT = 2;
export const GBR_DE_MAX_PARTNERS_COUNT = 20;

export const GBR_LEGAL_CODE = 'DE101';
export const EGBR_LEGAL_CODE = 'DE111';
export const VEREINE_LEGAL_CODE = 'DE602';
export const PARTG_LEGAL_CODE = 'DE105';
export const PARTGMBB_LEGAL_CODE = 'DE106';
export const EK_LEGAL_CODE = 'DE001';
export const EINZELUNTERNEHMER_LEGAL_CODE = 'DE002';
export const FREIBERUFLER_LEGAL_CODE = 'DE210';
export const GMBH_LEGAL_CODE = 'DE206';
export const GMBH_IG_LEGAL_CODE = 'DE306';
export const UG_LEGAL_CODE = 'DE207';
export const UG_IG_LEGAL_CODE = 'DE307';

export const AT_EINZELUNTERNEHMER_LEGAL_CODE = 'ATCUST301';

export const ZELFSTANDIGE_LEGAL_CODE = 'BECUST201';
export const BE_COMMV_LEGAL_CODE = 'BE012';
export const BE_NV_LEGAL_CODE = 'BE014';
export const BE_VZW_LEGAL_CODE = 'BE017';

export const EENMANSZAAK_LEGAL_CODE = 'NLCUST208';
export const NL_BV_LEGAL_CODE = 'NL101'; // Besloten Vennootschap (BV)
export const NL_VOF_LEGAL_CODE = 'NL304'; // Vennootschap Onder Firm (VOF)
export const NL_STICHTING_LEGAL_CODE = 'NL206'; // Stichting (Foundation)
export const ENI_LEGAL_CODE = 'PTCUST183';
export const TRABALHADOR_INDEPENDENTE = 'PTCUST184';

export const GERMAN_FREELANCER_LEGAL_FORMS = [
  EINZELUNTERNEHMER_LEGAL_CODE,
  FREIBERUFLER_LEGAL_CODE,
] as const;

export const GERMAN_SOLO_LEGAL_FORMS = [
  EINZELUNTERNEHMER_LEGAL_CODE,
  FREIBERUFLER_LEGAL_CODE,
  EK_LEGAL_CODE,
] as const;

export const UNIPERSONAL_CODES = [
  FREIBERUFLER_LEGAL_CODE,
  EINZELUNTERNEHMER_LEGAL_CODE,
  'IT002',
  'ESC01',
  AT_EINZELUNTERNEHMER_LEGAL_CODE,
  ZELFSTANDIGE_LEGAL_CODE,
  EENMANSZAAK_LEGAL_CODE,
  ENI_LEGAL_CODE,
  TRABALHADOR_INDEPENDENTE,
] as const;

export const NON_POWERABLE_CREDITSAFE_CODES = [
  '5720',
  '5498',
  '1500',
  '1000',
  ...GERMAN_SOLO_LEGAL_FORMS,
] as const;

export const FRENCH_CAPITAL_DEPOSIT_LEGAL_FORMS = [
  '5720',
  '5710',
  '5499',
  '5498',
  '5485',
  '5202',
  '6540',
  '6599',
] as const;

export const ORGANIZATIONS_WITH_LEGAL_SECTOR = [
  GBR_LEGAL_CODE,
  EINZELUNTERNEHMER_LEGAL_CODE,
  FREIBERUFLER_LEGAL_CODE,
] as const;

export const GERMAN_CAPITAL_DEPOSIT_COMPANY_TYPES = [
  { value: 'DE306', dropdownLabel: 'GmbH in Gründung (i.G.)' },
  { value: 'DE307', dropdownLabel: 'UG in Gründung (i.G.)' },
] as const;

export const GERMAN_EXISTING_COMPANY_TYPES = [
  { value: 'DE206', dropdownLabel: 'GmbH' },
  { value: 'DE002', dropdownLabel: 'Einzelunternehmer' },
  { value: 'DE101', dropdownLabel: 'GbR' },
  { value: EGBR_LEGAL_CODE, dropdownLabel: 'eGbR' },
  { value: VEREINE_LEGAL_CODE, dropdownLabel: 'e.V.' },
  { value: 'DE105', dropdownLabel: 'PartG' },
  { value: 'DE106', dropdownLabel: 'PartG mbB' },
  { value: 'DE207', dropdownLabel: 'UG' },
  { value: 'DE001', dropdownLabel: 'e.K.' },
  { value: 'DE210', dropdownLabel: 'Freiberufler' },
  { value: 'DE208', dropdownLabel: 'gGmbH' },
  { value: 'DE201', dropdownLabel: 'AG' },
  { value: 'DE107', dropdownLabel: 'GmbH & Co. KG' },
  { value: 'DE103', dropdownLabel: 'Kommanditgesellschaft (KG)' },
  { value: 'DE102', dropdownLabel: 'oHG' },
  { value: 'DE108', dropdownLabel: 'AG & Co. KG' },
  { value: 'DE112', dropdownLabel: 'UG & Co. KG' },
  { value: 'DE202', dropdownLabel: 'KGaA' },
  { value: 'DE110', dropdownLabel: 'GmbH & Co. oHG' },
  { value: 'DE201:110', dropdownLabel: 'AG & Co. oHG' },
] as const;

export const NETHERLANDS_AND_BELGIUM_UBO_LEGAL_FORMS = [
  { value: 'NL101', dropdownLabel: 'Besloten Vennootschap (BV)' },
  { value: 'NL206', dropdownLabel: 'Stichting' },
  { value: 'NL304', dropdownLabel: 'Vennootschap Onder Firm (VOF)' },
  { value: 'BE610', dropdownLabel: 'Besloten Vennootschap (BV)' },
  { value: 'BE011', dropdownLabel: 'Vennootschap onder Firma (VOF)' },
  { value: 'BE012', dropdownLabel: 'Gewone commanditaire vennootschap (Comm. V)' },
  { value: 'BE014', dropdownLabel: 'Naamloze vennootschap (NV)' },
  { value: 'BE017', dropdownLabel: 'Vereniging zonder winstoogmerk (VZW)' },
] as const;

export const NETHERLANDS_AND_BELGIUM_UBO_LEGAL_FORMS_CODES =
  NETHERLANDS_AND_BELGIUM_UBO_LEGAL_FORMS.map(item => item.value);

// Sorted by most used
export const GERMAN_ALL_COMPANY_TYPES = [
  { value: 'DE002', dropdownLabel: 'Einzelunternehmer' },
  { value: 'DE210', dropdownLabel: 'Freiberufler' },
  { value: 'DE206', dropdownLabel: 'GmbH' },
  { value: 'DE306', dropdownLabel: 'GmbH in Gründung (i.G.)' }, // Under registration
  { value: 'DE207', dropdownLabel: 'UG' },
  { value: 'DE307', dropdownLabel: 'UG in Gründung (i.G.)' }, // Under registration
  { value: 'DE101', dropdownLabel: 'GbR' },
  { value: EGBR_LEGAL_CODE, dropdownLabel: 'eGbR' },
  { value: VEREINE_LEGAL_CODE, dropdownLabel: 'e.V.' },
  { value: 'DE105', dropdownLabel: 'PartG' },
  { value: 'DE106', dropdownLabel: 'PartG mbB' },
  { value: 'DE001', dropdownLabel: 'e.K.' },
  { value: 'DE208', dropdownLabel: 'gGmbH' },
  { value: 'DE201', dropdownLabel: 'AG' },
  { value: 'DE107', dropdownLabel: 'GmbH & Co. KG' },
  { value: 'DE103', dropdownLabel: 'Kommanditgesellschaft (KG)' },
  { value: 'DE102', dropdownLabel: 'oHG' },
  { value: 'DE108', dropdownLabel: 'AG & Co. KG' },
  { value: 'DE112', dropdownLabel: 'UG & Co. KG' },
  { value: 'DE202', dropdownLabel: 'KGaA' },
  { value: 'DE110', dropdownLabel: 'GmbH & Co. oHG' },
  { value: 'DE201:110', dropdownLabel: 'AG & Co. oHG' },
] as const;

export const UNAVAILABLE_GERMAN_COMPANY_TYPE_ON_CREDITSAFE = [
  'DE002', // Einzelunternehmer -> Freelancer
  'DE210', // Freiberufler -> Self employed
  'DE306', // GmbH in Gründung (i.G.) -> Under registration
  'DE307', // UG in Gründung (i.G.) -> Under registration
  'DE101', // GbR -> Association
] as const;

export const ITALIAN_EXISTING_COMPANY_TYPES = [
  { value: 'IT001', dropdownLabel: 'Imprenditore individuale (in possesso di visura camerale)' },
  { value: 'IT002', dropdownLabel: 'Libero professionista/Lavoratore autonomo' },
  { value: 'SE', dropdownLabel: 'Società semplice (S.s.)' },
  { value: 'SN', dropdownLabel: 'Società in nome collettivo (S.n.c.)' },
  { value: 'AS', dropdownLabel: 'Società in accomandita semplice (S.a.s.)' },
  { value: 'SP', dropdownLabel: 'Società per azioni (S.p.a.)' },
  { value: 'AA', dropdownLabel: 'Società in accomandita per azioni (S.a.p.a.)' },
  { value: 'SR', dropdownLabel: 'Società a responsabilità limitata (S.r.l.)' },
  { value: 'SU', dropdownLabel: 'Società a responsabilità limitata con un socio unico' },
  { value: 'RS', dropdownLabel: 'Società a responsabilità limitata semplificata (S.r.l.s)' },
  { value: 'SC', dropdownLabel: 'Società cooperativa' },
] as const;

export const PORTUGUESE_EXISTING_COMPANY_TYPES = [
  {
    value: 'PT121',
    dropdownLabel: 'Sociedade por Quotas (Lda)',
    icon: civilCompanyIcon,
  },
  { value: 'PTCUST184', dropdownLabel: 'Trabalhador Independente', icon: freelancerIcon },
  {
    value: 'PTCUST183',
    dropdownLabel: 'Empresário em Nome Individual (ENI)',
    icon: microbusinessIcon,
  },
] as const;

export const PORTUGUESE_EXISTING_FREELANCERS = ['PTCUST184', 'PTCUST183'];

export const INDIVIDUAL_TYPES = [
  ...PORTUGUESE_EXISTING_FREELANCERS,
  AT_EINZELUNTERNEHMER_LEGAL_CODE,
  ZELFSTANDIGE_LEGAL_CODE,
  EENMANSZAAK_LEGAL_CODE,
];

export const AUSTRIAN_EXISTING_COMPANY_TYPES = [
  { value: 'ATCUST301', dropdownLabel: 'Einzelunternehmer' },
  { value: 'AT202', dropdownLabel: 'Gesellschaft mit beschränkter Haftung (GmbH)' },
  { value: 'AT103', dropdownLabel: 'Kommanditgesellschaften (KG)' },
  { value: 'AT102', dropdownLabel: 'Offene Gesellschaften (OG)' },
  { value: 'AT203', dropdownLabel: 'FlexCo' },
] as const;

export const ITALIAN_COMPANY_CREATION_TYPES = [
  { value: 'SR', dropdownLabel: 'Società a responsabilità limitata (S.r.l.)' },
  { value: 'SU', dropdownLabel: 'Società a responsabilità limitata con un socio unico' },
  { value: 'RS', dropdownLabel: 'Società a responsabilità limitata semplificata (S.r.l.s)' },
] as const;

export const SPANISH_EXISTING_COMPANY_TYPES = [
  {
    value: 'ESC07',
    dropdownLabel: 'Sociedad de Responsabilidad Limitada (SL)',
  },
  { value: 'ESC01', dropdownLabel: 'Empresario individual (autónomo)' },
  { value: 'ESC04', dropdownLabel: 'Sociedad Anónima (SA)' },
  {
    value: 'ESC02',
    dropdownLabel: 'Emprendedor de Responsabilidad Limitada (ERL)',
  },
  { value: 'ESC06', dropdownLabel: 'Sociedad Anónima Laboral (SAL)' },
  { value: 'ESC11', dropdownLabel: 'Sociedad Colectiva (SC)' },
  { value: 'ESC13', dropdownLabel: 'Sociedad Comanditaria por Acciones (SCA)' },
  { value: 'ESC12', dropdownLabel: 'Sociedad Comanditaria Simple (SCS)' },
  {
    value: 'ESC09',
    dropdownLabel: 'Sociedad de Responsabilidad Limitada Laboral (SLL)',
  },
  {
    value: 'ESC15',
    dropdownLabel: 'Sociedad Limitada de Formación Sucesiva (SLFS)',
  },
  { value: 'ESC10', dropdownLabel: 'Sociedad Limitada Nueva Empresa (SLNE)' },
  { value: 'ESC30', dropdownLabel: 'Sociedad Profesionales (SAP/SAL)' },
  { value: 'ESC14', dropdownLabel: 'Sucursal de entidad extranjera' },
] as const;

export const BUSINESS_INFOS_FIELDS = [
  'name',
  'legalNumber',
  'legalFormCreditsafeCode',
  'legalRegistrationDate',
] as const;

export const ES_CC_BUSINESS_INFOS_FIELDS = ['name'] as const;

export const K_DEP_BUSINESS_FINANCES_FIELDS = [
  'yearlyTransactionVolume',
  'maxSinglePaymentAmount',
] as const;

// `annualTurnover` and `estimatedAnnualTurnover` will never be shown simultaneously.
// `estimatedAnnualTurnover` is shown only for Italian organizations but will replace `annualTurnover` in the future.
export const BUSINESS_FINANCES_FIELDS = [
  'annualTurnover',
  'estimatedAnnualTurnover',
  ...K_DEP_BUSINESS_FINANCES_FIELDS,
] as const;

export const NATURE_OF_OPERATIONS_OPTIONS = {
  CORE_BANKING: 'core_banking',
  INVOICING: 'invoicing',
  SPEND_MANAGEMENT: 'spend_management',
  BOOK_KEEPING: 'book_keeping',
  REMUNERATION: 'remuneration',
  CASHFLOW_MANAGEMENT: 'cashflow_management',
  PAYROLL_REIMBURSEMENT: 'payroll_reimbursement',
} as const;

export type NatureOfOperation =
  (typeof NATURE_OF_OPERATIONS_OPTIONS)[keyof typeof NATURE_OF_OPERATIONS_OPTIONS];

export const AVAILABLE_PRICE_PLAN_PATTERNS = [
  `${PRODUCT_GROUP_CODES.TeamBusiness}_`,
  `${PRODUCT_GROUP_CODES.TeamEssential}_`,
  `${PRODUCT_GROUP_CODES.TeamEnterprise}_`,
  `${PRODUCT_GROUP_CODES.SoloPremium}_`,
  `${PRODUCT_GROUP_CODES.SoloSmart}_`,
  `${PRODUCT_GROUP_CODES.SoloBasic}_`,
  `${PRODUCT_GROUP_CODES.Starter}_`,
] as const;

export const SKIP_PRICING_PATTERNS = [
  `${PRODUCT_GROUP_CODES.TeamBusiness}_`,
  `${PRODUCT_GROUP_CODES.TeamEnterprise}_`,
] as const;

export const AVAILABLE_PRICE_PLAN_RECURRENCES = ['monthly', 'annual'] as const;

export const GBR_MANAGEMENT_TYPES = {
  SINGLE: 'single',
  JOINT: 'joint',
} as const;

export const ITALIAN_LIMITED_COMPANY_CREDITSAFE_CODES = ['SP', 'AA', 'SR', 'SU', 'RS'] as const;
export const ITALIAN_PARTNERSHIP_CREDITSAFE_CODES = ['SE', 'SN', 'AS', 'SF', 'SI', 'CE'] as const;
export const ITALIAN_COOPERATION_CREDITSAFE_CODES = [
  'PC',
  'PS',
  'CL',
  'SC',
  'CI',
  'OO',
  'MA',
] as const;
export const ITALIAN_FOUNDATION_CREDITSAFE_CODES = ['FI', 'FO'] as const;

export const COMPANY_STATUS = {
  REGISTERED: 'registered',
  UNREGISTERED: 'unregistered',
} as const;

export const COMPANY_STATUS_LIST = [
  COMPANY_STATUS.REGISTERED,
  COMPANY_STATUS.UNREGISTERED,
] as const;

export type CompanyStatus = (typeof COMPANY_STATUS)[keyof typeof COMPANY_STATUS];

export type AustrianExistingCompanyType = (typeof AUSTRIAN_EXISTING_COMPANY_TYPES)[number]['value'];
export type GermanAllCompanyType = (typeof GERMAN_ALL_COMPANY_TYPES)[number]['value'];
export type PortugueseAllCompanyType = (typeof PORTUGUESE_EXISTING_COMPANY_TYPES)[number]['value'];
export type FranceAllCompanyType = (typeof FRENCH_LEGAL_FORMS)[number];
